// Sortier- und filterbare Tabelle im Hausdesign (Glas-Container, TableSortLabel-Kopfzeile).
// Spalten: { id, label, get(row), render?(row), align?, hideBelow?: 'sm'|'md', sortable?: false, width?, nowrap? }
// filters: { options: [{value,label}], match(row, value) } → Chip-Leiste über der Tabelle
import { useMemo, useState } from 'react'
import { Box, Chip, InputAdornment, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Clear, Search } from '@mui/icons-material'
import './datatable.css'

const collator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' })

const compare = (a, b) => {
    if (a == null && b == null) return 0
    if (a == null) return 1      // leere Werte immer ans Ende
    if (b == null) return -1
    if (typeof a === 'number' && typeof b === 'number') return a - b
    if (a instanceof Date && b instanceof Date) return a - b
    return collator.compare(String(a), String(b))
}

export function DataTable({ columns, rows, rowKey, defaultSort, defaultDir = 'asc', searchFields, searchLabel = 'Suchen',
                            filters, onRowClick, rowClass, emptyText = 'Keine Einträge', toolbar, search: controlledSearch, onSearch }) {
    const [orderBy, setOrderBy] = useState(defaultSort)
    const [dir, setDir] = useState(defaultDir)
    // Suchtext wahlweise von außen gesteuert (z. B. wenn der Aufrufer ihn für einen Export braucht)
    const [internalSearch, setInternalSearch] = useState('')
    const search = controlledSearch ?? internalSearch
    const setSearch = onSearch ?? setInternalSearch
    const [activeFilter, setActiveFilter] = useState(filters?.options?.[0]?.value ?? null)
    const theme = useTheme()
    const belowSm = useMediaQuery(theme.breakpoints.down('sm'))
    const belowMd = useMediaQuery(theme.breakpoints.down('md'))

    const visibleColumns = columns.filter(c => !(c.hideBelow === 'sm' && belowSm) && !(c.hideBelow === 'md' && belowMd))

    const handleSort = (id) => {
        if (orderBy === id) setDir(dir === 'asc' ? 'desc' : 'asc')
        else { setOrderBy(id); setDir('asc') }
    }

    const shown = useMemo(() => {
        const needle = search.trim().toLowerCase()
        let list = rows
        if (filters && activeFilter != null) list = list.filter(r => filters.match(r, activeFilter))
        if (needle && searchFields?.length)
            list = list.filter(r => searchFields.some(f => String(f(r) ?? '').toLowerCase().includes(needle)))
        const col = columns.find(c => c.id === orderBy)
        if (col) {
            const sortValue = col.sortValue || col.get
            list = [...list].sort((a, b) => (dir === 'asc' ? 1 : -1) * compare(sortValue(a), sortValue(b)))
        }
        return list
    }, [rows, search, activeFilter, orderBy, dir, columns, filters, searchFields])

    return (
        <Box className='datatable'>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} className='datatable-toolbar'>
                {searchFields?.length > 0 &&
                    <TextField size='small' label={searchLabel} value={search} onChange={e => setSearch(e.target.value)} sx={{ flexGrow: 1, minWidth: 180 }}
                        InputProps={{
                            startAdornment: <InputAdornment position='start'><Search fontSize='small' /></InputAdornment>,
                            endAdornment: search && <InputAdornment position='end'><Clear fontSize='small' style={{ cursor: 'pointer' }} onClick={e => setSearch('')} /></InputAdornment>,
                        }} />
                }
                {filters &&
                    <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                        {filters.options.map(o =>
                            <Chip key={String(o.value)} label={o.label} size='small' clickable
                                color={activeFilter === o.value ? 'primary' : 'default'}
                                variant={activeFilter === o.value ? 'filled' : 'outlined'}
                                onClick={e => setActiveFilter(o.value)} />
                        )}
                    </Stack>
                }
                {toolbar}
            </Stack>
            <TableContainer className='datatable-container'>
                <Table size='small' stickyHeader>
                    <TableHead>
                        <TableRow>
                            {visibleColumns.map(c =>
                                <TableCell key={c.id} align={c.align || 'left'} style={{ width: c.width }} sortDirection={orderBy === c.id ? dir : false}>
                                    {c.sortable === false ? c.label :
                                        <TableSortLabel active={orderBy === c.id} direction={orderBy === c.id ? dir : 'asc'} onClick={e => handleSort(c.id)}>
                                            {c.label}
                                        </TableSortLabel>
                                    }
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {shown.map(row =>
                            <TableRow key={rowKey(row)} hover={!!onRowClick} onClick={onRowClick ? (e => onRowClick(row)) : undefined}
                                className={'datatable-row ' + (rowClass ? rowClass(row) || '' : '') + (onRowClick ? ' clickable' : '')}>
                                {visibleColumns.map(c =>
                                    <TableCell key={c.id} align={c.align || 'left'} style={{ whiteSpace: c.nowrap ? 'nowrap' : undefined }}>{c.render ? c.render(row) : c.get(row)}</TableCell>
                                )}
                            </TableRow>
                        )}
                        {shown.length === 0 &&
                            <TableRow><TableCell colSpan={visibleColumns.length} align='center' className='datatable-empty'>{emptyText}</TableCell></TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography variant='caption' color='text.secondary' className='datatable-count'>
                {shown.length === rows.length ? `${rows.length} Einträge` : `${shown.length} von ${rows.length} Einträgen`}
            </Typography>
        </Box>
    )
}
